import {
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useRef,
  type ComponentType,
  type ReactNode,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router'
import { AppError } from '@/components/layout/AppError'
import { AppHeader } from '@/components/layout/AppHeader'
import { LoadingState } from '@/components/feedback/LoadingState'
import { loadQuietly } from '@/lib/reloadOnStaleBuild'

const LandmarkListPage = lazy(() =>
  import('@/features/landmarks/LandmarkListPage').then((m) => ({
    default: m.LandmarkListPage,
  })),
)
// The landmark page, once its code has loaded (preloaded, or opened once)
let loadedDetailsPage: ComponentType | undefined
const loadDetailsPage = () =>
  import('@/features/landmark-details/LandmarkDetailsPage').then((m) => {
    loadedDetailsPage = m.LandmarkDetailsPage
    return { default: m.LandmarkDetailsPage }
  })
const LazyDetailsPage = lazy(loadDetailsPage)
/** Renders the loaded page directly: lazy() suspends on first render even when it's loaded */
function LandmarkDetailsPage() {
  const Details = loadedDetailsPage ?? LazyDetailsPage
  return <Details />
}
const AccountPage = lazy(() =>
  import('@/features/account/AccountPage').then((m) => ({
    default: m.AccountPage,
  })),
)
const NotFound = lazy(() =>
  import('@/components/layout/NotFound').then((m) => ({ default: m.NotFound })),
)

/**
 * Each page in its own Suspense boundary. Navigation runs in a transition, and React keeps
 * an already-shown boundary's content (here, the previous page) on screen while the next
 * page's code loads, with no sign that anything is happening. A new boundary shows its
 * fallback, so the spinner appears as soon as a landmark is opened. The keys make each
 * page's boundary a new one: routes render in the same place, so React would reuse it.
 */
function Page({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <Suspense fallback={<LoadingState label="Loading the page" />}>
      {children}
    </Suspense>
  )
}

export default function App() {
  const { pathname } = useLocation()
  const navigationType = useNavigationType()
  const previousPathnameRef = useRef(pathname)

  // Fetch the details page's code once the browser is idle, so opening the first landmark
  // waits only for its data. Quietly: a failed preload mustn't reload the list
  useEffect(() => {
    const preload = () => void loadQuietly(loadDetailsPage).catch(() => {})
    if (typeof requestIdleCallback === 'undefined') {
      const timer = setTimeout(preload, 2000)
      return () => clearTimeout(timer)
    }
    const id = requestIdleCallback(preload, { timeout: 5000 })
    return () => cancelIdleCallback(id)
  }, [])

  // A link or redirect to another page starts at the top; otherwise the new page keeps the old
  // page's scroll position. Query-string changes (tabs, paging, filters) keep the position,
  // back/forward (POP) keeps the browser's scroll restoration, and the list restores its own
  // position when returning from a landmark.
  useLayoutEffect(() => {
    if (pathname === previousPathnameRef.current) return
    previousPathnameRef.current = pathname
    if (navigationType !== 'POP') window.scrollTo(0, 0)
  }, [pathname, navigationType])

  return (
    <>
      <AppHeader />
      <main>
        {/* Reset the error boundary when the route changes */}
        <ErrorBoundary FallbackComponent={AppError} resetKeys={[pathname]}>
          <Routes>
            <Route
              path="/"
              element={
                <Page key="list">
                  <LandmarkListPage />
                </Page>
              }
            />
            <Route
              path="/landmarks/:lpNumber"
              element={
                <Page key="details">
                  <LandmarkDetailsPage />
                </Page>
              }
            />
            <Route
              path="/account"
              element={
                <Page key="account">
                  <AccountPage />
                </Page>
              }
            />
            {/* Old details URL (the landmark was held in app state, not the URL) */}
            <Route path="/details" element={<Navigate to="/" replace />} />
            <Route
              path="*"
              element={
                <Page key="not-found">
                  <NotFound />
                </Page>
              }
            />
          </Routes>
        </ErrorBoundary>
      </main>
    </>
  )
}
